import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import Header from "./Header";
import { api } from "../server";

function Drivers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("ACTIVE");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const formRef = useRef(null);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [areas, setAreas] = useState([]);
  const [areaName, setAreaName] = useState("");
  const [areaPincode, setAreaPincode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isAvailable, setIsAvailable] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/admin/drivers");
      setRows(data?.drivers || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setPhone("");
    setEmail("");
    setPassword("");
    setVehicleNumber("");
    setLicenseNumber("");
    setAreas([]);
    setAreaName("");
    setAreaPincode("");
    setIsActive(true);
    setIsAvailable(true);
  };
  const openCreate = () => {
    resetForm();
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const openEdit = (r) => {
    setEditing(r);
    setName(r.name || "");
    setPhone(r.phone || "");
    setEmail(r.email || "");
    setPassword("");
    setVehicleNumber(r.vehicleNumber || "");
    setLicenseNumber(r.licenseNumber || "");
    setAreas(Array.isArray(r.assignedAreas) ? r.assignedAreas : []);
    setAreaName("");
    setAreaPincode("");
    setIsActive(!!r.isActive);
    setIsAvailable(!!r.isAvailable);
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const closeForm = () => {
    setOpenForm(false);
    resetForm();
  };

  const addArea = () => {
    if (!areaName.trim() || !areaPincode.trim()) return;
    setAreas((prev) => [
      ...prev,
      {
        areaName: areaName.trim(),
        pincode: areaPincode.trim(),
        isActive: true,
      },
    ]);
    setAreaName("");
    setAreaPincode("");
  };
  const removeArea = (idx) =>
    setAreas((prev) => prev.filter((_, i) => i !== idx));

  const saveDriver = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Name is required.");
    if (!/^[6-9]\d{9}$/.test(phone))
      return alert("Enter a valid 10-digit phone number.");
    if (!vehicleNumber.trim()) return alert("Vehicle number is required.");
    if (!editing && (!password || password.length < 6))
      return alert("Password (min 6 chars) is required.");

    const payload = {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim() || null,
      vehicleNumber: vehicleNumber.trim().toUpperCase(),
      licenseNumber: licenseNumber.trim() || null,
      assignedAreas: areas,
      isActive,
      isAvailable,
    };
    if (password) payload.password = password;

    setSaving(true);
    try {
      if (editing) {
        const data = await api.put(
          `/api/admin/drivers/${editing._id}`,
          payload,
        );
        const updated = data?.driver;
        setRows((prev) =>
          prev.map((x) => (x._id === editing._id ? { ...x, ...updated } : x)),
        );
      } else {
        const data = await api.post("/api/admin/drivers", payload);
        const created = data?.driver;
        if (created) setRows((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id) => {
    const r = rows.find((x) => x._id === id);
    if (!r) return;
    try {
      const data = await api.put(`/api/admin/drivers/${id}`, {
        isActive: !r.isActive,
      });
      const updated = data?.driver;
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, ...updated } : x)),
      );
    } catch (err) {
      alert(err.message || "Update failed");
    }
  };
  const removeDriver = async (id) => {
    if (!window.confirm("Deactivate this driver?")) return;
    try {
      await api.delete(`/api/admin/drivers/${id}`);
      setRows((prev) =>
        prev.map((x) =>
          x._id === id ? { ...x, isActive: false, isAvailable: false } : x,
        ),
      );
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows
      .filter((r) => {
        const matchQ =
          !query ||
          String(r.name || "")
            .toLowerCase()
            .includes(query) ||
          String(r.phone || "")
            .toLowerCase()
            .includes(query) ||
          String(r.vehicleNumber || "")
            .toLowerCase()
            .includes(query);
        const matchActive =
          active === "ALL"
            ? true
            : active === "ACTIVE"
              ? !!r.isActive
              : !r.isActive;
        return matchQ && matchActive;
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [rows, q, active]);

  const stats = useMemo(() => {
    const total = rows.length;
    const act = rows.filter((x) => x.isActive).length;
    const avail = rows.filter((x) => x.isActive && x.isAvailable).length;
    return { total, active: act, inactive: total - act, available: avail };
  }, [rows]);

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Drivers</h3>
                <div className="text-secondary lh-1">
                  Delivery partners • Vehicles • Service areas
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={() => {
                    setQ("");
                    setActive("ACTIVE");
                  }}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button className="btn btn-primary px-4" onClick={openCreate}>
                  <i className="bi bi-plus-circle me-2" />
                  Add Driver
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-truck" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Active</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.active}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-lightning" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Available</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.available}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-pause-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Inactive</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.inactive}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-search" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Search & Filters</div>
                    <div className="text-secondary small">
                      Find by name, phone, or vehicle
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-9">
                    <label className="form-label mb-1 fw-normal">Search</label>
                    <input
                      className="form-control"
                      placeholder="Name / phone / vehicle number"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label mb-1 fw-normal">Status</label>
                    <select
                      className="form-select"
                      value={active}
                      onChange={(e) => setActive(e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="ALL">All</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {openForm && (
              <div
                ref={formRef}
                className="card border-0 shadow-sm rounded-4 mt-2"
              >
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                        style={{ width: 40, height: 40 }}
                      >
                        <i
                          className={`bi ${editing ? "bi-pencil-square" : "bi-truck"}`}
                        />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">
                          {editing ? "Edit Driver" : "Add Driver"}
                        </div>
                        <div className="text-secondary small">
                          {editing
                            ? "Update driver details and service areas"
                            : "Register a new delivery partner"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                      onClick={closeForm}
                    >
                      <i className="bi bi-x-lg me-1" />
                      Close
                    </button>
                  </div>

                  <form onSubmit={saveDriver}>
                    <div className="row g-2">
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Name *
                        </label>
                        <input
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Driver name"
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Phone *
                        </label>
                        <input
                          className="form-control"
                          value={phone}
                          maxLength={10}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="10-digit mobile"
                          required
                          disabled={!!editing}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="optional@email.com"
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Password {editing ? "(leave blank to keep)" : "*"}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={editing ? "•••••" : "min 6 chars"}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          Vehicle Number *
                        </label>
                        <input
                          className="form-control text-uppercase"
                          value={vehicleNumber}
                          onChange={(e) => setVehicleNumber(e.target.value)}
                          placeholder="MH 12 AB 1234"
                          required
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label mb-1 fw-normal">
                          License Number
                        </label>
                        <input
                          className="form-control"
                          value={licenseNumber}
                          onChange={(e) => setLicenseNumber(e.target.value)}
                          placeholder="DL number"
                        />
                      </div>

                      <div className="col-12">
                        <hr className="my-2" />
                        <label className="form-label mb-2 fw-semibold">
                          Assigned Service Areas
                        </label>
                        <div className="row g-2">
                          <div className="col-12 col-md-5">
                            <input
                              className="form-control"
                              placeholder="Area name (e.g. Boring Road)"
                              value={areaName}
                              onChange={(e) => setAreaName(e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <input
                              className="form-control"
                              placeholder="Pincode"
                              value={areaPincode}
                              onChange={(e) => setAreaPincode(e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-3">
                            <button
                              type="button"
                              className="btn btn-light border w-100"
                              onClick={addArea}
                            >
                              <i className="bi bi-plus-lg me-1" /> Add Area
                            </button>
                          </div>
                        </div>
                        {areas.length > 0 && (
                          <div className="mt-3 d-flex flex-wrap gap-1">
                            {areas.map((a, i) => (
                              <span
                                key={i}
                                className="badge bg-info-subtle text-info p-2 d-inline-flex align-items-center gap-2"
                              >
                                <i className="bi bi-geo-alt" />
                                {a.areaName} • {a.pincode}
                                <i
                                  className="bi bi-x-lg"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removeArea(i)}
                                />
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="form-check form-switch mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="drvActive"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="drvActive"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="form-check form-switch mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="drvAvail"
                            checked={isAvailable}
                            onChange={(e) => setIsAvailable(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="drvAvail"
                          >
                            Available for orders
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-3 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light px-4"
                        onClick={closeForm}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={saving}
                      >
                        {saving && (
                          <span className="spinner-border spinner-border-sm me-2" />
                        )}
                        {saving
                          ? "Saving..."
                          : editing
                            ? "Update Driver"
                            : "Save Driver"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No drivers found
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => (
                          <tr key={r._id}>
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Name</span> &mdash;{" "}
                              {r.name}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {r.phone}
                              <br />
                              <span className="text-dark">
                                Vehicle
                              </span> &mdash; {r.vehicleNumber}
                              <br />
                              <span className="text-dark">
                                License
                              </span> &mdash; {r.licenseNumber || "—"}
                              <br />
                              <span className="text-dark">
                                Areas
                              </span> &mdash;{" "}
                              {Array.isArray(r.assignedAreas) &&
                              r.assignedAreas.length > 0 ? (
                                <>
                                  {r.assignedAreas.slice(0, 3).map((a, i) => (
                                    <span
                                      key={i}
                                      className="badge bg-info-subtle text-info me-1"
                                    >
                                      {a.areaName} ({a.pincode})
                                    </span>
                                  ))}
                                  {r.assignedAreas.length > 3 && (
                                    <span className="badge bg-light text-dark">
                                      +{r.assignedAreas.length - 3}
                                    </span>
                                  )}
                                </>
                              ) : (
                                "—"
                              )}
                              <br />
                              <span className="text-dark">
                                Status
                              </span> &mdash;{" "}
                              {r.isActive ? (
                                <span className="text-success">Active</span>
                              ) : (
                                <span className="text-danger">Inactive</span>
                              )}
                              {r.isActive && (
                                <>
                                  {" "}
                                  &mdash;{" "}
                                  <span
                                    className={
                                      r.isAvailable
                                        ? "text-primary"
                                        : "text-secondary"
                                    }
                                  >
                                    {r.isAvailable ? "Available" : "Busy"}
                                  </span>
                                </>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                <span
                                  onClick={() => openEdit(r)}
                                  className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() => toggleActive(r._id)}
                                  className={`badge cursor-pointer px-4 rounded-3 btn-py-sm ${
                                    r.isActive
                                      ? "bg-warning-soft"
                                      : "bg-success-soft"
                                  }`}
                                >
                                  {r.isActive ? "Deactivate" : "Activate"}
                                </span>
                                <span
                                  onClick={() => removeDriver(r._id)}
                                  className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default Drivers;
